import * as React from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import axios, { AxiosError } from 'axios';

import { Button } from 'components/Button';
import InputField from 'components/Form/InputField';
import { FormProvider } from 'components/Form/Form';
import Icon from 'components/icon';
import { Modal } from 'my-account/components/Modal';

import ErrorList from 'tempcontrol/components/error-list';

const downloadFormSchema = z.object({
  customer_name: z.string({ required_error: 'Your name is required.' }).min(1, 'Your name is required.').max(255),
  customer_email: z
    .string({ required_error: 'Your email is required.' })
    .min(1, 'Your email is required.')
    .email('Please enter a valid email address.')
    .max(255),
  customer_company: z.string().max(255).optional(),
});

export type DownloadFormValues = z.infer<typeof downloadFormSchema>;

export interface DownloadModalProps {
  isOpen: boolean;
  isSubmitting: boolean;
  /**
   * True while the queue worker is rendering the PDF (after POST
   * /download succeeded but before GET /download streams the binary).
   * The modal swaps the CTA copy to "Preparing your PDF…" so the
   * user gets visible feedback during the SPB-style async render.
   */
  isRendering?: boolean;
  defaultValues?: Partial<DownloadFormValues>;
  onRequestClose: () => void;
  onSubmit: (values: DownloadFormValues) => Promise<void>;
}

/**
 * Lead-capture modal styled after `modal-gate.php`. Persists visitor
 * details onto the project and triggers the async PDF pipeline.
 */
export const DownloadModal: React.VFC<DownloadModalProps> = ({
  isOpen,
  isSubmitting,
  isRendering = false,
  defaultValues,
  onRequestClose,
  onSubmit,
}) => {
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const formMethods = useForm<DownloadFormValues>({
    mode: 'onBlur',
    resolver: zodResolver(downloadFormSchema),
    defaultValues: {
      customer_name: defaultValues?.customer_name ?? '',
      customer_email: defaultValues?.customer_email ?? '',
      customer_company: defaultValues?.customer_company ?? '',
    },
  });

  React.useEffect(() => {
    if (!isOpen) return;
    setSubmitError(null);
    formMethods.reset({
      customer_name: defaultValues?.customer_name ?? '',
      customer_email: defaultValues?.customer_email ?? '',
      customer_company: defaultValues?.customer_company ?? '',
    });
  }, [isOpen, defaultValues, formMethods]);

  const handleSubmit = formMethods.handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      await onSubmit(values);
    } catch (e) {
      const message = await formatError(e);
      setSubmitError(message);
    }
  });

  const isBusy = isSubmitting || isRendering;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={isBusy ? () => undefined : onRequestClose}
      id="modal-gate"
      className="c-modal__wrapper c-modal__wrapper--sm"
      style={{
        content: { maxWidth: '41.25rem', display: 'block', minHeight: 'auto', outline: 'none' },
      }}
    >
      <div className="c-modal">
        {/* eslint-disable-next-line jsx-a11y/anchor-has-content, jsx-a11y/anchor-is-valid */}
        <a
          href="#"
          className="c-modal-close"
          onClick={(event) => {
            event.preventDefault();
            if (!isBusy) onRequestClose();
          }}
        />

        <div className="c-modal__main">
          <div className="c-block c-block--spacing-t-small c-block--spacing-b-small">
            <div className="o-container-fluid">
              <div className="c-block__header u-mb-spacer-base-small">
                <p className="c-headline">Download Your Results</p>
                <h3 className="c-title u-mb-spacer-base-small">Get Your Spec Sheet</h3>
                <p>
                  Share a few details so we can generate a PDF of this sizing summary for your records and keep it on
                  your project for later.
                </p>
              </div>

              <FormProvider {...formMethods} hasFloatingLabels>
                <form className="c-form--float-labels fl-form" onSubmit={handleSubmit} noValidate>
                  <div className="o-row o-row--small-gutters">
                    <div className="o-col-12">
                      <InputField
                        name="customer_name"
                        elStyle="fill"
                        label="Your Name"
                        placeholder="Your Name"
                        icon={<Icon name="user" className="o-svg-icon" />}
                      />
                    </div>
                    <div className="o-col-12">
                      <InputField
                        name="customer_email"
                        type="email"
                        elStyle="fill"
                        label="Email Address"
                        placeholder="Email Address"
                        icon={<Icon name="envelope" className="o-svg-icon" />}
                      />
                    </div>
                    <div className="o-col-12">
                      <InputField
                        name="customer_company"
                        elStyle="fill"
                        label="Company"
                        placeholder="Company"
                        icon={<Icon name="office" className="o-svg-icon" />}
                      />
                    </div>

                    {submitError && (
                      <div className="o-col-12">
                        <ErrorList messages={[submitError]} />
                      </div>
                    )}

                    {isRendering && (
                      <div className="o-col-12">
                        <p className="c-note u-mb-0">
                          Preparing your PDF on our servers — this usually takes a few seconds. Hang tight.
                        </p>
                      </div>
                    )}

                    <div className="o-col-12@sm">
                      <div className="c-form-footer">
                        <Button
                          type="submit"
                          variant="secondary"
                          isLoading={isBusy}
                          rightIcon={isRendering ? undefined : 'arrow'}
                        >
                          {isRendering ? 'Preparing PDF…' : 'Start Download'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </form>
              </FormProvider>

              <div className="c-widget c-widget--divider u-pt-3 u-mt-spacer-base-large">
                <div className="c-notes u-my-0">
                  <p>
                    We use your contact details only to attach this project to your download and follow up if you need
                    help with the recommendation.
                  </p>
                </div>
              </div>

              <div className="c-widget c-widget--divider u-pt-3 u-mt-spacer-base u-text-right">
                <button
                  type="button"
                  className="c-button c-button--secondary"
                  onClick={onRequestClose}
                  disabled={isBusy}
                >
                  <span>Continue</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const formatError = async (error: unknown): Promise<string> => {
  if (axios.isAxiosError(error)) {
    const raw = (error as AxiosError).response?.data;
    let parsed: { message?: string; errors?: Record<string, string[]> } | null = null;
    if (raw instanceof Blob) {
      try {
        const text = await raw.text();
        parsed = JSON.parse(text);
      } catch {
        parsed = null;
      }
    } else if (raw && typeof raw === 'object') {
      parsed = raw as { message?: string; errors?: Record<string, string[]> };
    }
    if (parsed?.errors) {
      const messages = Object.values(parsed.errors).flat();
      if (messages.length > 0) return messages.join(' ');
    }
    if (parsed?.message) return parsed.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return 'Could not generate your PDF right now. Please try again.';
};

export default DownloadModal;
